"use client";
import { useRouter } from "next/navigation";
import { useEffect, useState, useCallback } from "react";
import { performAuthenticatedCall } from "lib/utils";

function useAuthenticatedApi<T>(
  route: string,
  query: string,
  method: "GET" | "POST" | "DELETE" | "PUT",
  body?: string,
  shouldTriggerCall: boolean = true
) {
  const [result, setResult] = useState<T>();
  const [isCallFinished, setIsCallFinished] = useState<boolean>();
  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<number>();

  const router = useRouter();

  const performCall = useCallback(async () => {
    setIsCallFinished(false);

    if (!shouldTriggerCall) return;

    try {
      const result = await performAuthenticatedCall<T>(
        route,
        query,
        method,
        body
      );

      setResult(result);
    } catch (error) {
      if ((error as unknown as { statusCode: number })?.statusCode === 401) {
        router.push("/login");
        return;
      }
      
      setErrorStatus((error as unknown as { statusCode: number })?.statusCode);
      setIsApiError(true);
    } finally {
      setIsCallFinished(true);
    }
  }, [body, method, query, route, router, shouldTriggerCall]);

  useEffect(() => {
    performCall();
  }, [body, method, performCall, query, route, router, shouldTriggerCall]);

  return {
    data: result,
    refresh: performCall,
    isCallFinished,
    isApiError,
    errorStatus,
  };
}

export default useAuthenticatedApi;
